import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAnnouncements } from "./AnnoucementsContext";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <div style={{ flex: 1 }} />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

const AnnouncementsList = () => {
  const {
    announcements,
    setSelectedAnnouncement,
    setSelectedAnnouncementLog,
    authenticationChecked,
  } = useAnnouncements();
  const [error, setError] = React.useState("");

  const dateRangeOperator = {
    label: "is between",
    value: "between",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value || filterItem.value.length !== 2) {
        return null;
      }

      return (params) => {
        const [startDate, endDate] = filterItem.value;
        if (!startDate || !endDate || !params.value) {
          return true;
        }

        const cellDate = new Date(params.value);
        const start = new Date(startDate);
        const end = new Date(endDate);

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        return cellDate >= start && cellDate <= end;
      };
    },
    InputComponent: ({ item, applyValue, focusElementRef }) => {
      const [startDate, setStartDate] = React.useState(item.value?.[0] || null);
      const [endDate, setEndDate] = React.useState(item.value?.[1] || null);

      const handleStartDateChange = (newValue) => {
        setStartDate(newValue);
        applyValue({ ...item, value: [newValue, endDate] });
      };

      const handleEndDateChange = (newValue) => {
        setEndDate(newValue);
        applyValue({ ...item, value: [startDate, newValue] });
      };

      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1,
              minWidth: 200,
            }}
          >
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  inputRef={focusElementRef}
                />
              )}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              renderInput={(params) => <TextField {...params} size="small" />}
            />
          </Box>
        </LocalizationProvider>
      );
    },
  };

  const viewEditLinkIcon = (item) => {
    return (
      <Link
        className={"hyper-links"}
        onClick={(e) => {
          setSelectedAnnouncement(item);
        }}
      >
        {item.id}
      </Link>
    );
  };

  const convertDateISOToUTC = (isoDate) => {
    if (isoDate !== null && isoDate !== "") {
      const date = new Date(isoDate);
      const utcFormatted = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")} ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}:${String(date.getUTCSeconds()).padStart(2, "0")}`;
      return utcFormatted;
    } else return "";
  };

  const dataGridColumns = [
    {
      field: "id",
      headerName: "Id",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 100,
      renderCell: (params) => viewEditLinkIcon(params.row),
    },
    {
      field: "title",
      headerName: "Title",
      filterable: true,
      sortable: true,
      flex: 1,
      minWidth: 100,
    },
    {
      field: "agency",
      headerName: "Agency",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 50,
    },
    {
      field: "author",
      headerName: "Author",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 50,
    },
    {
      field: "priority",
      headerName: "Priority",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 50,
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 100,
      renderCell: (params) => convertDateISOToUTC(params.row.expirationDate),
    },
    {
      field: "lastUpdate",
      headerName: "Last Updated",
      filterable: true,
      sortable: true,
      flex: 0.2,
      minWidth: 100,
      type: "date",
      valueGetter: (params) =>
        params.row.lastUpdate ? new Date(params.row.lastUpdate) : null,
      renderCell: (params) => convertDateISOToUTC(params.row.lastUpdate),
      filterOperators: [
        dateRangeOperator,
        {
          label: "is",
          value: "is",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return (params) => {
              if (!params.value) return false;
              const cellDate = new Date(params.value);
              const filterDate = new Date(filterItem.value);
              cellDate.setHours(0, 0, 0, 0);
              filterDate.setHours(0, 0, 0, 0);
              return cellDate.getTime() === filterDate.getTime();
            };
          },
          InputComponent: ({ item, applyValue, focusElementRef }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={item.value || null}
                onChange={(newValue) =>
                  applyValue({ ...item, value: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    inputRef={focusElementRef}
                  />
                )}
              />
            </LocalizationProvider>
          ),
        },
        {
          label: "after",
          value: "after",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return (params) => {
              if (!params.value) return false;
              const cellDate = new Date(params.value);
              const filterDate = new Date(filterItem.value);
              filterDate.setHours(23, 59, 59, 999);
              return cellDate > filterDate;
            };
          },
          InputComponent: ({ item, applyValue, focusElementRef }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={item.value || null}
                onChange={(newValue) =>
                  applyValue({ ...item, value: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    inputRef={focusElementRef}
                  />
                )}
              />
            </LocalizationProvider>
          ),
        },
        {
          label: "before",
          value: "before",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return (params) => {
              if (!params.value) return false;
              const cellDate = new Date(params.value);
              const filterDate = new Date(filterItem.value);
              filterDate.setHours(0, 0, 0, 0);
              return cellDate < filterDate;
            };
          },
          InputComponent: ({ item, applyValue, focusElementRef }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={item.value || null}
                onChange={(newValue) =>
                  applyValue({ ...item, value: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    inputRef={focusElementRef}
                  />
                )}
              />
            </LocalizationProvider>
          ),
        },
      ],
    },
  ];

  if (announcements != null) {
    if (announcements.length > 0)
      return (
        <DataGrid
          rows={announcements}
          columns={dataGridColumns}
          autoHeight
          getRowHeight={() => "auto"}
          components={{
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "1rem",
              fontWeight: "bold",
              display: "flex",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "1.5",
            },
          }}
        />
      );
    else
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h5">
          {error ? error : "No Data Found"}
        </Typography>
      );
  } else {
    if (authenticationChecked === "Success") {
      return (
        <Box sx={{ width: "100%", textAlign: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (authenticationChecked === "UNAUTHORIZED") {
      const wasAdminLoggedIn = sessionStorage.getItem("isAdmin") === "true";
      const isUserLoggedIn = sessionStorage.getItem("isUser") === "true";
      if (isUserLoggedIn)
        return (
          <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
            You are not authorized to view this page.
          </Typography>
        );
      else if (wasAdminLoggedIn)
        return (
          <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
            Session expired.{" "}
            <Link to="/About/AdminLogin" className="hyper-links">
              <b>Click here to log back in</b>
            </Link>
          </Typography>
        );
      else return <Navigate to="/About/AdminLogin" replace />;
    }
    if (authenticationChecked === "FORBIDDEN")
      return <Navigate to="/About/AdminLogin" replace />;
  }
};

export default AnnouncementsList;
